import { LoginOutlined } from "@ant-design/icons";
import { ORG_SLUGS_LOCALSTORAGE_KEY } from "@p0security/frontend-core";
import {
  Button,
  Card,
  Divider,
  Flex,
  Input,
  Spacing,
  Typography,
} from "@p0security/ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { sortBy } from "lodash";
import moment from "moment";
import { ChangeEvent, Fragment, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { spacing } from "styles/spacing";

import { Splash } from "../Splash";

const MAX_ORGS_TO_DISPLAY = 5;

/** Renders an org selector
 *
 * Clicking an org simply navigates to that org's base URL.
 *
 * If the URL includes a 'path' parameter, will navigate to that path,
 * relative to the org's base URL. All other parameters will be
 * passed transparently.
 */
export const OrganizationSelect: React.FC = () => {
  const flags = useFlags();
  const [addingNewOrg, setAddingNewOrg] = useState<boolean>(false);
  const [searchParams] = useSearchParams();

  const previousOrgs = useMemo(() => {
    const localStorageOrgs = localStorage.getItem(ORG_SLUGS_LOCALSTORAGE_KEY);
    if (localStorageOrgs === null) return [];
    const parsed: [string, any][] = Object.entries(
      JSON.parse(localStorageOrgs)
    );
    const sorted = sortBy(
      parsed,
      ([_, org]) => -new Date(org.lastAccess)
    ).slice(0, MAX_ORGS_TO_DISPLAY);
    return sorted;
  }, []);

  const forceAddOrg = useMemo(
    () => Object.keys(previousOrgs).length === 0 || addingNewOrg,
    [addingNewOrg, previousOrgs]
  );

  const addNewOrg = useCallback(() => setAddingNewOrg(true), []);

  const { path, passParams } = useMemo(() => {
    const path = searchParams.get("path");
    searchParams.delete("path");
    const passParams = searchParams.toString();
    return { path, passParams };
  }, [searchParams]);

  return (
    <Splash>
      <Card>
        {previousOrgs ? (
          <Spacing asChild mb={spacing.scale.xl}>
            <div style={{ textAlign: "center" }}>
              <Typography.Title level={4}>Welcome back!</Typography.Title>
              <Spacing asChild mb={spacing.scale.md}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "max-content 1fr",
                    gap: spacing.scale.sm,
                    alignItems: "center",
                  }}
                >
                  {previousOrgs.map(([key, org], ix) => (
                    <Fragment key={key}>
                      <Button
                        className="w-full"
                        type="default"
                        href={`/o/${key}${path ? `/${path}` : ""}${
                          passParams ? `?${passParams}` : ""
                        }`}
                      >
                        <LoginOutlined /> {key}
                      </Button>
                      <Typography.Text type="secondary" className="text-right">
                        {ix === 0 ? "Last accessed (on this device) " : "... "}
                        {moment(org.lastAccess).fromNow()}
                      </Typography.Text>
                    </Fragment>
                  ))}
                </div>
              </Spacing>
              {!forceAddOrg && (
                <Button type="link" onClick={addNewOrg}>
                  Sign into a different organization
                </Button>
              )}
            </div>
          </Spacing>
        ) : null}
        {forceAddOrg && <NewOrganization />}
        {flags.allowSignups ? (
          <>
            <Divider> or </Divider>
            <div className="flex justify-center">
              <Button type="primary" href="/create-account">
                Create Organization
              </Button>
            </div>
          </>
        ) : null}
      </Card>
    </Splash>
  );
};

const NewOrganization: React.FC<object> = () => {
  const [orgId, setOrgId] = useState<string>();
  const navigate = useNavigate();

  const onOrgIdChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setOrgId(event.target.value.trim());
    },
    [setOrgId]
  );

  const onClickLogin = useCallback(() => {
    navigate(`o/${orgId}`);
  }, [orgId, navigate]);

  const onKeyUpValue = useCallback(
    (event: any) => {
      if (event.key === "Enter") {
        navigate(`o/${orgId}`);
      }
    },
    [orgId, navigate]
  );

  return (
    <Flex vertical gap="small">
      <div>Please enter your organization ID</div>
      <Input onChange={onOrgIdChange} onKeyUp={onKeyUpValue} />
      <Button
        className="w-fit"
        icon={<LoginOutlined />}
        type="primary"
        disabled={!orgId || orgId === ""}
        onClick={onClickLogin}
      >
        Login
      </Button>
    </Flex>
  );
};
